import type { ReactElement } from "react"
import SurfaceOrg from "@onestore/hel/dist/components/organisms/SurfaceOrg"
import type {
  Color,
  SpacingSpace,
} from "@onestore/hel/dist/components/organisms/SurfaceOrg/SurfaceOrg.types"
import ThankYouMessage from "@gatsby-plugin-generic-page/components/ThankYouMessage"
import type { Formily as FormilyType } from "@gatsby-plugin-generic-page/fragments/formily"
import type { FormilyWithThankYou } from "@gatsby-plugin-generic-page/fragments/formilyWithThankYou"
import type { HubspotFormType } from "@gatsby-plugin-generic-page/fragments/hubspotForm"
import type { ThankYouMessageElement } from "@gatsby-plugin-generic-page/fragments/thankYouMessage"
import { SquidexFormType } from "@gatsby-plugin-generic-page/types/formType"
import { typeMatches } from "@onestore-graphql"
import SalesManagoForm from "~/components/SalesManagoForm"
import isEmpty from "~/lib/isEmpty"
import type { FormSalesManago, Forms as FormsType } from "~/types/squidex"
import Formily from "../Formily/Formily"
import HubspotForm from "../HubspotForm"

type Props = {
  forms: FormsType[]
  formBackgroundColor?: Color
  spacingSpace?: SpacingSpace
}

function Forms({
  forms,
  formBackgroundColor,
  spacingSpace,
}: Props): ReactElement<Props> | null {
  const { __typename, flatData } = forms[0] as FormsType

  if (typeMatches(__typename, SquidexFormType.HUBSPOT_FORM)) {
    const { formId, title } = flatData as HubspotFormType["flatData"]

    return (
      <SurfaceOrg
        color={!isEmpty(formBackgroundColor) ? formBackgroundColor : undefined}
        spacingSpace={typeof spacingSpace === "number" ? spacingSpace : 6}
        shadow="medium"
        borderRadiusSize="xlarge"
        role="form"
      >
        <HubspotForm formId={formId} title={title} />
      </SurfaceOrg>
    )
  }

  if (typeMatches(__typename, SquidexFormType.FORM_SALES_MANAGO)) {
    const { link, id } = flatData as FormSalesManago["flatData"]

    return <SalesManagoForm src={link} id={id} />
  }

  const getFormilyFormElement = (
    formilyFormFlatData: FormilyType["flatData"],
    thankYouMessage?: ThankYouMessageElement
  ) => {
    const { formButton, captcha, ...formilyFlatData } = formilyFormFlatData

    const thankYouMessageElement = !isEmpty(thankYouMessage) ? (
      <ThankYouMessage {...thankYouMessage[0].flatData} />
    ) : null

    return (
      <SurfaceOrg
        color={!isEmpty(formBackgroundColor) ? formBackgroundColor : undefined}
        spacingSpace={typeof spacingSpace === "number" ? spacingSpace : 6}
        shadow="small"
        role="form"
      >
        <div>
          <Formily
            button={formButton}
            captchaEnabled={captcha}
            customThankYouPage={thankYouMessageElement}
            {...formilyFlatData}
          />
        </div>
      </SurfaceOrg>
    )
  }

  if (typeMatches(__typename, SquidexFormType.FORMILY_FORM)) {
    return getFormilyFormElement(flatData as FormilyType["flatData"])
  }

  if (typeMatches(__typename, SquidexFormType.FORMILY_FORM_WITH_THANK_YOU)) {
    const { formilyForm, thankYouMessage } =
      flatData as FormilyWithThankYou["flatData"]

    return getFormilyFormElement(formilyForm[0].flatData, thankYouMessage)
  }

  return null
}

export default Forms
